/**
 * Per-party branding rules. Pure helpers only: no database, no UI copy.
 * Callers map issue and warning codes to translated strings.
 */

export const PARTY_BRANDING_NAME_MAX = 80;
export const PARTY_BRANDING_TAGLINE_MAX = 160;
export const PARTY_BRANDING_WELCOME_MAX = 400;
export const PARTY_BRANDING_TEXT_CONTRAST = 4.5;

export const PARTY_BRAND_DARK_SURFACE = "#0E0C0A";
export const PARTY_BRAND_LIGHT_SURFACE = "#FAF6EE";

const BLACK = "#000000";
const WHITE = "#FFFFFF";

export type PartyBranding = {
  enabled: boolean;
  displayName: string;
  tagline: string | null;
  welcomeMessage: string | null;
  logoUrl: string | null;
  iconUrl: string | null;
  heroImageUrl: string | null;
  primaryColor: string;
  accentColor: string;
  showPoweredBy: boolean;
};

export type PartyBrandingInput = {
  enabled: boolean;
  displayName: string;
  tagline?: string | null;
  welcomeMessage?: string | null;
  logoUrl?: string | null;
  iconUrl?: string | null;
  heroImageUrl?: string | null;
  primaryColor: string;
  accentColor: string;
  showPoweredBy: boolean;
};

export type PartyBrandingIssueCode =
  | "displayNameRequired"
  | "displayNameTooLong"
  | "taglineTooLong"
  | "welcomeMessageTooLong"
  | "primaryColorInvalid"
  | "accentColorInvalid"
  | "logoUrlInvalid"
  | "iconUrlInvalid"
  | "heroImageUrlInvalid";

export type PartyBrandingWarningCode =
  | "accentAdjustedForDark"
  | "accentAdjustedForLight";

export type PartyBrandingTheme = {
  primary: string;
  accent: string;
  onPrimary: "#000000" | "#FFFFFF";
  onAccent: "#000000" | "#FFFFFF";
  accentOnDark: string;
  accentOnLight: string;
  warnings: PartyBrandingWarningCode[];
  /** Page background: a deep shade of the primary so the whole party feels on-brand. */
  canvas: string;
  /** Cards and panels on the canvas. */
  surface: string;
  /** Inputs, hover states, and selected cards. */
  surfaceRaised: string;
  /** Accent tuned to stay AA-readable on every brand surface. */
  highlight: string;
  onHighlight: "#000000" | "#FFFFFF";
  /** Main call-to-action fill. Prefers the primary, then the accent. */
  cta: string;
  onCta: "#000000" | "#FFFFFF";
  ctaHover: string;
  /** Secondary text on the primary color that still meets AA. */
  onPrimaryMuted: string;
  primaryIsLight: boolean;
};

export type PartyBrandingValidation =
  | {
      ok: true;
      branding: PartyBranding;
      theme: PartyBrandingTheme;
    }
  | { ok: false; errors: PartyBrandingIssueCode[] };

export function normalizeHexColor(input: string): string | null {
  const trimmed = input.trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{3}$/.test(trimmed)) {
    const expanded = trimmed
      .split("")
      .map((char) => char + char)
      .join("");
    return `#${expanded.toUpperCase()}`;
  }
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) {
    return `#${trimmed.toUpperCase()}`;
  }
  return null;
}

function srgbChannel(hex: string, index: number): number {
  const value = parseInt(hex.slice(1 + index * 2, 3 + index * 2), 16) / 255;
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number {
  const normalized = normalizeHexColor(hex);
  if (!normalized) {
    throw new Error("invalidHex");
  }
  const red = srgbChannel(normalized, 0);
  const green = srgbChannel(normalized, 1);
  const blue = srgbChannel(normalized, 2);
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

export function contrastRatio(foreground: string, background: string): number {
  const first = relativeLuminance(foreground);
  const second = relativeLuminance(background);
  const lighter = Math.max(first, second);
  const darker = Math.min(first, second);
  return (lighter + 0.05) / (darker + 0.05);
}

export function readableForeground(background: string): "#000000" | "#FFFFFF" {
  const onBlack = contrastRatio(BLACK, background);
  const onWhite = contrastRatio(WHITE, background);
  return onBlack >= onWhite ? BLACK : WHITE;
}

export function mixHex(from: string, to: string, amount: number): string {
  const start = normalizeHexColor(from);
  const end = normalizeHexColor(to);
  if (!start || !end) {
    throw new Error("invalidHex");
  }
  const ratio = Math.min(1, Math.max(0, amount));
  const channels = [0, 1, 2].map((index) => {
    const origin = parseInt(start.slice(1 + index * 2, 3 + index * 2), 16);
    const target = parseInt(end.slice(1 + index * 2, 3 + index * 2), 16);
    return Math.round(origin + (target - origin) * ratio)
      .toString(16)
      .padStart(2, "0");
  });
  return `#${channels.join("")}`.toUpperCase();
}

function readableAccentOnSurface(accent: string, surface: string): {
  color: string;
  adjusted: boolean;
} {
  if (contrastRatio(accent, surface) >= PARTY_BRANDING_TEXT_CONTRAST) {
    return { color: accent, adjusted: false };
  }
  const target = readableForeground(surface);
  let mixed = accent;
  for (let step = 1; step <= 20; step += 1) {
    mixed = mixHex(accent, target, step / 20);
    if (contrastRatio(mixed, surface) >= PARTY_BRANDING_TEXT_CONTRAST) {
      return { color: mixed, adjusted: true };
    }
  }
  return { color: target, adjusted: true };
}

/**
 * Moves `foreground` toward `background` as far as possible while keeping
 * `minContrast`. Used for secondary text that should feel quieter but stay AA.
 */
export function mutedForeground(
  foreground: string,
  background: string,
  minContrast: number = PARTY_BRANDING_TEXT_CONTRAST,
): string {
  const start = normalizeHexColor(foreground);
  if (!start) {
    throw new Error("invalidHex");
  }
  let best = start;
  for (let step = 1; step <= 10; step += 1) {
    const candidate = mixHex(start, background, step / 20);
    if (contrastRatio(candidate, background) < minContrast) break;
    best = candidate;
  }
  return best;
}

/** Luminance ceilings keep cream body text at 7:1 or better on brand surfaces. */
const CANVAS_MAX_LUMINANCE = 0.009;
const SURFACE_MAX_LUMINANCE = 0.018;
const SURFACE_RAISED_MAX_LUMINANCE = 0.028;
const NON_TEXT_CONTRAST = 3;
const LIGHT_PRIMARY_LUMINANCE = 0.4;

function shadeToLuminance(color: string, toward: string, max: number, from: number): string {
  for (let step = 0; step <= 50; step += 1) {
    const amount = from + ((1 - from) * step) / 50;
    const mixed = mixHex(color, toward, amount);
    if (relativeLuminance(mixed) <= max) return mixed;
  }
  return normalizeHexColor(toward) ?? toward;
}

function liftSurface(base: string, primary: string, tint: number, lift: number, max: number): string {
  const lifted = mixHex(mixHex(base, primary, tint), WHITE, lift);
  if (relativeLuminance(lifted) <= max) return lifted;
  return shadeToLuminance(lifted, base, max, 0);
}

function brandSurfaces(primary: string) {
  const canvas = shadeToLuminance(primary, PARTY_BRAND_DARK_SURFACE, CANVAS_MAX_LUMINANCE, 0.55);
  const surface = liftSurface(canvas, primary, 0.1, 0.035, SURFACE_MAX_LUMINANCE);
  const surfaceRaised = liftSurface(surface, primary, 0.08, 0.04, SURFACE_RAISED_MAX_LUMINANCE);
  return { canvas, surface, surfaceRaised };
}

function pickCta(primary: string, accent: string, canvas: string, highlight: string): string {
  for (const candidate of [primary, accent]) {
    if (contrastRatio(candidate, canvas) >= NON_TEXT_CONTRAST) return candidate;
  }
  return highlight;
}

function ctaHoverColor(cta: string, onCta: string): string {
  const away = mixHex(cta, onCta === WHITE ? BLACK : WHITE, 0.14);
  if (away !== normalizeHexColor(cta)) return away;
  return mixHex(cta, onCta, 0.08);
}

export function resolvePartyBrandingTheme(
  branding: Pick<PartyBranding, "primaryColor" | "accentColor">,
): PartyBrandingTheme {
  const primary = normalizeHexColor(branding.primaryColor) ?? branding.primaryColor;
  const accent = normalizeHexColor(branding.accentColor) ?? branding.accentColor;
  const onDark = readableAccentOnSurface(accent, PARTY_BRAND_DARK_SURFACE);
  const onLight = readableAccentOnSurface(accent, PARTY_BRAND_LIGHT_SURFACE);
  const warnings: PartyBrandingWarningCode[] = [];
  if (onDark.adjusted) warnings.push("accentAdjustedForDark");
  if (onLight.adjusted) warnings.push("accentAdjustedForLight");
  const { canvas, surface, surfaceRaised } = brandSurfaces(primary);
  const highlight = readableAccentOnSurface(accent, surfaceRaised).color;
  const cta = pickCta(primary, accent, canvas, highlight);
  const onCta = readableForeground(cta);
  const onPrimary = readableForeground(primary);
  return {
    primary,
    accent,
    onPrimary,
    onAccent: readableForeground(accent),
    accentOnDark: onDark.color,
    accentOnLight: onLight.color,
    warnings,
    canvas,
    surface,
    surfaceRaised,
    highlight,
    onHighlight: readableForeground(highlight),
    cta,
    onCta,
    ctaHover: ctaHoverColor(cta, onCta),
    onPrimaryMuted: mutedForeground(onPrimary, primary),
    primaryIsLight: relativeLuminance(primary) >= LIGHT_PRIMARY_LUMINANCE,
  };
}

export function partyBrandingCssProperties(
  theme: PartyBrandingTheme,
): Record<string, string> {
  return {
    "--party-brand-primary": theme.primary,
    "--party-brand-accent": theme.accent,
    "--party-brand-on-primary": theme.onPrimary,
    "--party-brand-on-accent": theme.onAccent,
    "--party-brand-accent-on-dark": theme.accentOnDark,
    "--party-brand-accent-on-light": theme.accentOnLight,
    "--party-brand-canvas": theme.canvas,
    "--party-brand-surface": theme.surface,
    "--party-brand-surface-raised": theme.surfaceRaised,
    "--party-brand-highlight": theme.highlight,
    "--party-brand-on-highlight": theme.onHighlight,
    "--party-brand-cta": theme.cta,
    "--party-brand-on-cta": theme.onCta,
    "--party-brand-cta-hover": theme.ctaHover,
    "--party-brand-on-primary-muted": theme.onPrimaryMuted,
  };
}

function optionalText(
  value: string | null | undefined,
  max: number,
  tooLong: PartyBrandingIssueCode,
  errors: PartyBrandingIssueCode[],
): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.length > max) {
    errors.push(tooLong);
    return null;
  }
  return trimmed;
}

export function normalizeBrandingImageUrl(
  value: string | null | undefined,
): string | null | "invalid" {
  if (value == null) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
    if (trimmed.includes("..") || /[\s"'()\\]/.test(trimmed)) {
      return "invalid";
    }
    return trimmed;
  }
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "https:") return "invalid";
    if (/[\s"'()\\]/.test(url.toString())) return "invalid";
    return url.toString();
  } catch {
    return "invalid";
  }
}

export function validatePartyBrandingInput(
  input: PartyBrandingInput,
): PartyBrandingValidation {
  const errors: PartyBrandingIssueCode[] = [];
  const displayName = input.displayName.trim();
  if (!displayName) {
    errors.push("displayNameRequired");
  } else if (displayName.length > PARTY_BRANDING_NAME_MAX) {
    errors.push("displayNameTooLong");
  }

  const tagline = optionalText(
    input.tagline,
    PARTY_BRANDING_TAGLINE_MAX,
    "taglineTooLong",
    errors,
  );
  const welcomeMessage = optionalText(
    input.welcomeMessage,
    PARTY_BRANDING_WELCOME_MAX,
    "welcomeMessageTooLong",
    errors,
  );

  const primaryColor = normalizeHexColor(input.primaryColor);
  if (!primaryColor) errors.push("primaryColorInvalid");
  const accentColor = normalizeHexColor(input.accentColor);
  if (!accentColor) errors.push("accentColorInvalid");

  const logoUrl = readImage(input.logoUrl, "logoUrlInvalid", errors);
  const iconUrl = readImage(input.iconUrl, "iconUrlInvalid", errors);
  const heroImageUrl = readImage(input.heroImageUrl, "heroImageUrlInvalid", errors);

  if (errors.length > 0 || !primaryColor || !accentColor) {
    return { ok: false, errors };
  }

  const branding: PartyBranding = {
    enabled: input.enabled,
    displayName,
    tagline,
    welcomeMessage,
    logoUrl,
    iconUrl,
    heroImageUrl,
    primaryColor,
    accentColor,
    showPoweredBy: input.showPoweredBy,
  };

  return {
    ok: true,
    branding,
    theme: resolvePartyBrandingTheme(branding),
  };
}

function readImage(
  value: string | null | undefined,
  code: PartyBrandingIssueCode,
  errors: PartyBrandingIssueCode[],
): string | null {
  const normalized = normalizeBrandingImageUrl(value);
  if (normalized === "invalid") {
    errors.push(code);
    return null;
  }
  return normalized;
}

export function isPartyBrandingActive(
  branding: PartyBranding | null | undefined,
): branding is PartyBranding {
  return Boolean(branding?.enabled && branding.displayName.trim());
}

/**
 * A full logo is a wordmark and already shows the company name.
 * An icon, or no image, still needs the typed name beside it.
 */
export type PartyBrandMark = {
  imageUrl: string | null;
  imageIsWordmark: boolean;
  typedName: string | null;
};

export function partyBrandMark(
  branding: Pick<PartyBranding, "displayName" | "logoUrl" | "iconUrl">,
): PartyBrandMark {
  if (branding.logoUrl) {
    return {
      imageUrl: branding.logoUrl,
      imageIsWordmark: true,
      typedName: null,
    };
  }
  if (branding.iconUrl) {
    return {
      imageUrl: branding.iconUrl,
      imageIsWordmark: false,
      typedName: branding.displayName,
    };
  }
  return {
    imageUrl: null,
    imageIsWordmark: false,
    typedName: branding.displayName,
  };
}
